import { constantTimeCompare } from '../../helpers';
import { userCanDo, userOwns } from '../../vulcan-users/permissions';
import { userIsPostGroupOrganizer, userIsPostCoauthor } from './helpers';

export type CollaborativeEditingAccessLevel = "none"|"read"|"comment"|"edit";

export function strongerAccessLevel(a: CollaborativeEditingAccessLevel|null, b: CollaborativeEditingAccessLevel|null): CollaborativeEditingAccessLevel {
  if (a==="edit" || b==="edit") return "edit";
  if (a==="comment" || b==="comment") return "comment";
  if (a==="read" || b==="read") return "read";
  if (a) return a;
  if (b) return b;
  return "none";
}

export function accessLevelCan(accessLevel: CollaborativeEditingAccessLevel, operation: "read"|"comment"|"edit") {
  if (accessLevel === "edit") {
    return true;
  } else if (accessLevel === "comment") {
    return operation==="read" || operation==="comment";
  } else if (accessLevel === "read") {
    return operation==="read";
  } else if (accessLevel === "none") {
    return false;
  }
}

export function getSharingKeyFromContext(context: Pick<ResolverContext, 'searchParams'|'headers'>|null) {
  // During SSR, searchParams are the page's. Client-side GraphQL requests go
  // to a different URL, so the client forwards the page's key in a header.
  const key = context?.searchParams?.get('key') || context?.headers?.get('link-sharing-key');
  if (typeof key === 'string') {
    return key;
  }

  return '';
}

export async function getCollaborativeEditorAccess({formType, post, user, context, useAdminPowers}: {
  formType: "new"|"edit"|null,
  post: DbPost|null,
  user: DbUser|null,
  context: ResolverContext,
  
  // If true and the user is a moderator/admin, take their admin powers into
  // account. If false, return permissions as they would be given no moderator
  // powers.
  useAdminPowers: boolean,
}): Promise<CollaborativeEditingAccessLevel> {
  return getCollaborativeEditorAccessWithKey({
    formType,
    post,
    user,
    context,
    useAdminPowers,
    linkSharingKey: getSharingKeyFromContext(context),
  });
}

/**
 * Get collaborative editor access level, with an explicit link sharing key.
 * This is useful when the key is passed as a GraphQL argument rather than
 * being available in the request context.
 */
export async function getCollaborativeEditorAccessWithKey({formType, post, user, context, useAdminPowers, linkSharingKey}: {
  formType: "new"|"edit"|null,
  post: DbPost|null,
  user: DbUser|null,
  context: ResolverContext,
  useAdminPowers: boolean,
  linkSharingKey: string | null,
}): Promise<CollaborativeEditingAccessLevel> {
  // FIXME: There's a lot of redundancy between this function and
  // canUserEditPostMetadata in lib/collections/posts/helpers.ts, but they are
  // tricky to merge because of the `useAdminPowers` flag and because
  // `canUserEditPostMetadata` can't check for group-organizer status because it
  // isn't async. This function is used for controlling access to the body
  // (getting a ckEditor token).
  
  if (formType === "new" && user && !post) {
    return "edit";
  }
  if (!post) {
    return "none";
  }
  
  const canEditAsAdmin = useAdminPowers && userCanDo(user, 'posts.edit.all');
  const canEdit = userOwns(user, post) ||
    canEditAsAdmin ||
    await userIsPostGroupOrganizer(user, post, context) ||
    userIsPostCoauthor(user, post);
  let accessLevel: CollaborativeEditingAccessLevel = "none";
  
  if (canEdit) {
    accessLevel = strongerAccessLevel(accessLevel, "edit");
  } 

  const sharingSettings = post.sharingSettings ?? defaultSharingSettings;

  if (user && post.shareWithUsers.includes(user._id)) {
    accessLevel = strongerAccessLevel(accessLevel, sharingSettings.explicitlySharedUsersCan);
  }

  const canonicalLinkSharingKey = post.linkSharingKey;
  const keysMatch = !!canonicalLinkSharingKey && !!linkSharingKey && constantTimeCompare({ correctValue: canonicalLinkSharingKey, unknownValue: linkSharingKey });
  if (keysMatch) {
    accessLevel = strongerAccessLevel(accessLevel, sharingSettings.anyoneWithLinkCan);
  }
  
  return accessLevel;
}

export interface SharingSettings {
  anyoneWithLinkCan: CollaborativeEditingAccessLevel,
  explicitlySharedUsersCan: CollaborativeEditingAccessLevel,
}
export const defaultSharingSettings: SharingSettings = {
  anyoneWithLinkCan: "none",
  explicitlySharedUsersCan: "comment",
};
